/**
 * CustomerDetail — one customer, in the seven sections the control plane needs,
 * with each section saying which of them the product can currently answer.
 *
 * ===========================================================================
 * WHAT CHANGED WHEN THE TWO READS LANDED
 * ===========================================================================
 * This page used to render a PROVISIONING RECEIPT: the 201 body from the call
 * that created the organization, labelled with the instant it was measured and
 * with the fact that nothing could refresh it. That was the honest shape while
 * the only per-customer payload in existence was the one the create call
 * returned.
 *
 * Two reads now exist, so the snapshot is no longer the source:
 *
 *   IDENTITY   comes from the directory row — name, UUID, slug, status,
 *              timezone, created at. Read from `GET /api/system/customers`
 *              every time the list loads, not remembered from a create.
 *   ONBOARDING comes from `GET /api/system/customers/:orgId/onboarding`, which
 *              calls the identical `getOnboardingStatus` the customer's own
 *              administrator gets. There is no projection, no cache and no
 *              second copy of the step list on that path.
 *
 * The receipt survives for exactly one thing the reads do not carry: the first
 * administrator's invited state, the invitation expiry, the delivery job and
 * the mail provider. It is rendered as what it is — a reading of one instant,
 * with that instant printed beside it — and only for a customer provisioned
 * from this tab. For every other customer that block is an UNSERVED read, not a
 * blank.
 *
 * ===========================================================================
 * FIVE SECTIONS STILL HAVE NO PLATFORM READ AND SAY SO
 * ===========================================================================
 * People, Sites & Programs, Integrations, Jurisdictions and Activity are drawn
 * as declared gaps rather than as empty tables. `0 sites` here would mean "we
 * did not look", and the lane contract names that as the defect.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ConsoleClient, ConsoleOutcome } from '../platform/client.js';
import { IntegrationsSection } from './Connectors.js';
import { ReadinessSection, type ReadinessOutcome } from './Readiness.js';
import { FirstAdministrator } from './FirstAdministrator.js';
import type { IntegrationsOutcome } from './Connectors.js';
import type { PlatformActor } from '../platform/credentials.js';
import {
  administratorReport,
  describeMeasure,
  failureMeasure,
  liveState,
  onboardingProgress,
  provisioningBlockers,
  stepStates,
  unserved,
} from '../platform/customers.js';
import type { Measure } from '../platform/customers.js';
import {
  customerIntegrations,
  customerOnboarding,
  customerReadiness,
  readById,
} from '../platform/tenancy.js';
import type {
  CustomerIntegrationsRead,
  CustomerReadinessRead,
  CreatedCustomer,
  CustomerDirectoryEntry,
  CustomerOnboardingRead,
  PlatformRead,
} from '../platform/tenancy.js';

export interface ProvisioningReceipt {
  readonly created: CreatedCustomer;
  /** When the payload below was measured. Not when it was rendered. */
  readonly measuredAt: string;
}

/**
 * `null` means THE READ HAS NOT COME BACK YET, and it is deliberately not the
 * same value as a read that came back empty or a read that was refused. A
 * component that started at `{}` would render its loading state as a
 * measurement.
 */
export type OnboardingOutcome = ConsoleOutcome<CustomerOnboardingRead> | null;

export function UnservedSection({ heading, read }: { heading: string; read: PlatformRead }) {
  return (
    <section>
      <h3>{heading}</h3>
      <div className="card">
        <p>
          <strong>No platform read serves this.</strong>{' '}
          <code>
            {read.method} {read.route}
          </code>{' '}
          is not a route the API registers. This is NOT a measured zero and NOT a refusal — the
          question cannot be asked yet.
        </p>
        <p className="quiet">Held by: {read.heldBy}</p>
        <p className="quiet">It would supply: {read.supplies.join('; ')}.</p>
      </div>
    </section>
  );
}

/**
 * The onboarding read, in every state it can be in. FIVE OUTCOMES AND FIVE
 * SENTENCES — a refusal here must never look like a customer who has completed
 * nothing, which is exactly what an empty checklist would look like.
 */
export function OnboardingSection({ onboarding }: { onboarding: OnboardingOutcome }) {
  const read = readById('customer.onboarding');

  if (onboarding === null) {
    return (
      <section>
        <h3>Onboarding</h3>
        <p className="quiet">Reading {`${read.method} ${read.route}`}…</p>
      </section>
    );
  }

  if (onboarding.kind !== 'ok') {
    return (
      <section>
        <h3>Onboarding</h3>
        <div className="banner" role="alert">
          <strong>The onboarding read did not answer.</strong>
          <p>{describeMeasure(failureMeasure(onboarding))}</p>
          <p className="quiet">
            Nothing below is being shown as a step count on account of this. This customer’s
            onboarding state is UNKNOWN to this console right now — it is not zero, and it is not an
            empty checklist.
          </p>
        </div>
      </section>
    );
  }

  const server = onboarding.value.onboarding;
  const steps = stepStates(server);
  const progress = onboardingProgress(server, read);

  return (
    <section>
      <h3>Onboarding</h3>
      <p className="quiet">
        The canonical checklist, from the identical <code>getOnboardingStatus</code> the customer’s
        own administrator reads. Every row below came from the server; this console holds no step
        list of its own, and the denominator is the server’s <code>totalSteps</code> rather than a
        constant.
      </p>
      <p>
        Progress: <strong>{describeMeasure(progress)}</strong> · {server.percentComplete}%
        (server-reported)
      </p>
      <p>
        Current step reported by the server: <strong>{server.currentStep}</strong>
      </p>
      <ul>
        {steps.map((s) => (
          <li key={s.step}>
            <code>{s.step}</code> —{' '}
            {s.state === 'complete' ? (
              <>
                <strong>complete (server-reported)</strong>, completed at{' '}
                {describeMeasure(s.completedAt)}
              </>
            ) : s.state === 'current' ? (
              <>not complete — this is the current step</>
            ) : (
              <>not complete</>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CustomerDetail({
  customer,
  onboarding,
  receipt,
  integrations,
  readiness,
}: {
  customer: CustomerDirectoryEntry;
  onboarding: OnboardingOutcome;
  receipt?: ProvisioningReceipt;
  /** Connector status. Absent or null renders as "reading", never as zero. */
  integrations?: IntegrationsOutcome;
  /** Go-live readiness counts. Absent or null renders as "reading". */
  readiness?: ReadinessOutcome;
}) {
  const checklistRead = readById('customer.onboarding');
  const peopleRead = readById('customer.people');

  const server = onboarding && onboarding.kind === 'ok' ? onboarding.value.onboarding : null;

  /**
   * WHY THIS IS NOT `onboardingProgress(server, …)` ALONE.
   *
   * `server` is null in two very different situations: the read has not come
   * back, and the read was refused. Passing null to `onboardingProgress`
   * renders both as UNSERVED, which is a third thing again and is wrong for
   * both. So a failed read is turned into the measure that names what actually
   * happened, and only a genuinely absent read falls through to `unserved`.
   */
  const failure: Measure<string> | null =
    onboarding !== null && onboarding.kind !== 'ok'
      ? (failureMeasure(onboarding) as Measure<string>)
      : null;
  const pending: Measure<string> | null =
    onboarding === null
      ? { kind: 'unread', why: 'the onboarding read has not answered yet' }
      : null;

  const progress = failure ?? pending ?? onboardingProgress(server, checklistRead);
  const live = failure ?? pending ?? liveState(server, checklistRead);

  const admin = receipt
    ? administratorReport(
        receipt.created.administrator,
        receipt.created.continueSetup?.invitationUrl,
      )
    : null;
  const blockers = receipt ? provisioningBlockers(receipt.created.administrator) : null;

  return (
    <>
      <h2>{customer.name}</h2>

      <section>
        <h3>Overview</h3>
        <dl>
          <dt>Organization</dt>
          <dd>{customer.name}</dd>
          <dt>Organization UUID</dt>
          <dd>
            <code>{customer.id}</code>
          </dd>
          <dt>Slug</dt>
          <dd>
            <code>{customer.slug}</code>
          </dd>
          <dt>Status</dt>
          <dd>
            {/* SUSPENDED IS SHOWN, NOT HIDDEN. "Nobody can sign in" is exactly
                when an operator opens this console. */}
            <strong>{customer.status}</strong>
            {customer.status === 'active' ? null : (
              <> — every tenant sweep skips this organization while it is in this state.</>
            )}
          </dd>
          <dt>Timezone</dt>
          <dd>{customer.timezone}</dd>
          <dt>Created</dt>
          <dd>{customer.createdAt}</dd>
          <dt>Onboarding progress</dt>
          <dd>{describeMeasure(progress)}</dd>
          <dt>Live state</dt>
          <dd>{describeMeasure(live)}</dd>
        </dl>
      </section>

      <section>
        <h3>First administrator</h3>
        {receipt && admin && blockers ? (
          <>
            <p className="quiet">
              From the provisioning reply for this organization, measured at{' '}
              <code>{receipt.measuredAt}</code> and NOT re-read since — no platform route carries
              the invitation state, so this is a record of what provisioning returned rather than
              the state now.
            </p>
            <dl>
              <dt>Administrator</dt>
              <dd>
                {receipt.created.administrator.name} &lt;{receipt.created.administrator.email}&gt;
              </dd>
              <dt>State</dt>
              <dd>
                <strong>{describeMeasure(admin.state)}</strong>
              </dd>
              <dt>Invitation expires</dt>
              <dd>{describeMeasure(admin.invitationExpiresAt)}</dd>
              <dt>Invitation delivery job</dt>
              <dd>{describeMeasure(admin.deliveryJob)}</dd>
              <dt>Mail provider</dt>
              <dd>{describeMeasure(admin.mailProvider)}</dd>
              <dt>Invitation link</dt>
              <dd>{describeMeasure(admin.invitationLink)}</dd>
              <dt>Blockers</dt>
              <dd>
                {blockers.length === 0 ? (
                  <>
                    None found in the provisioning reply (delivery and mail provider were both
                    usable).
                  </>
                ) : (
                  <ul>
                    {blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </dl>
          </>
        ) : (
          <div className="card">
            <p>{describeMeasure(unserved<string>(peopleRead))}</p>
            <p className="quiet">
              This organization was not provisioned from this tab, so there is no reply here to read
              the first administrator’s invited state out of, and no platform route serves it. That
              is an absence of a READ, not an absence of an administrator.
            </p>
          </div>
        )}
      </section>

      <OnboardingSection onboarding={onboarding} />

      <UnservedSection heading="People" read={peopleRead} />
      <ReadinessSection outcome={readiness} />
      <IntegrationsSection outcome={integrations} />
      <UnservedSection heading="Jurisdictions" read={readById('customer.jurisdictions')} />
      <UnservedSection heading="Activity" read={readById('customer.activity')} />
    </>
  );
}

/**
 * The fetching half, kept separate so the rendering half above can be given
 * every outcome directly. A component that only existed with an effect inside
 * it could be tested in one state — the one an effect happens to reach — and
 * the refusal states are the ones that matter here.
 */
export function CustomerDetailLoader({
  customer,
  actor,
  client,
  receipt,
}: {
  customer: CustomerDirectoryEntry;
  actor: PlatformActor;
  client: ConsoleClient;
  receipt?: ProvisioningReceipt;
}) {
  const [onboarding, setOnboarding] = useState<OnboardingOutcome>(null);
  const [integrations, setIntegrations] = useState<IntegrationsOutcome>(null);
  const [readiness, setReadiness] = useState<ReadinessOutcome>(null);

  const load = useCallback(async () => {
    setOnboarding(null);
    setIntegrations(null);
    setReadiness(null);
    const [outcome, connectors, ready] = await Promise.all([
      client<CustomerOnboardingRead>(customerOnboarding(customer.id), actor),
      client<CustomerIntegrationsRead>(customerIntegrations(customer.id), actor),
      client<CustomerReadinessRead>(customerReadiness(customer.id), actor),
    ]);
    setOnboarding(outcome);
    setIntegrations(connectors);
    setReadiness(ready);
  }, [actor, client, customer.id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <CustomerDetail
        customer={customer}
        onboarding={onboarding}
        receipt={receipt}
        integrations={integrations}
        readiness={readiness}
      />
      {readiness?.kind === 'ok' ? (
        <FirstAdministrator
          orgId={customer.id}
          readiness={readiness.value.readiness}
          actor={actor}
          client={client}
          onChanged={() => void load()}
        />
      ) : null}
    </>
  );
}
